/*
 * call.stats from RTCPeerConnection.getStats(), and an estimated MOS with
 * the network part of the ITU-T G.107 E-model (same verdict thresholds as
 * the native core's diag.mos).
 */
import type { Json } from "./types";

export interface MosInput {
  rtt_ms?: number;
  jitter_ms?: number;
  loss_pct?: number;
  codec?: string;
}

export interface MosResult extends Json {
  mos: number;
  r: number;
  verdict: "excellent" | "good" | "fair" | "poor";
}

/* Ie (equipment impairment) and Bpl (packet-loss robustness) per codec:
 * G.113 Appendix I values for G.711 with PLC; Opus is wideband with
 * concealment and FEC, taken as transparent with high robustness. */
const CODECS: Record<string, { ie: number; bpl: number }> = {
  opus: { ie: 0, bpl: 30 }, // in-band FEC + deep PLC: more robust than G.711
  pcmu: { ie: 0, bpl: 25.1 },
  pcma: { ie: 0, bpl: 25.1 },
  g722: { ie: 0, bpl: 20 },
};

export function mosEstimate(inp: MosInput): MosResult {
  const rtt = Math.max(0, Number(inp.rtt_ms) || 0);
  const jitter = Math.max(0, Number(inp.jitter_ms) || 0);
  const loss = Math.min(100, Math.max(0, Number(inp.loss_pct) || 0));
  const name = String(inp.codec ?? "opus").toLowerCase().split("/")[0];
  const codec = CODECS[name] ?? CODECS.opus;

  // mouth-to-ear: half the round trip, a jitter buffer of ~2x jitter, plus
  // packetisation and processing (~40 ms)
  const d = rtt / 2 + 2 * jitter + 40;
  const id = 0.024 * d + (d > 177.3 ? 0.11 * (d - 177.3) : 0);
  const ieEff = codec.ie + (95 - codec.ie) * (loss / (loss + codec.bpl));
  let r = 93.2 - id - ieEff;
  r = Math.max(0, Math.min(100, r));
  let mos = 1 + 0.035 * r + r * (r - 60) * (100 - r) * 7e-6;
  mos = Math.max(1, Math.min(4.5, mos));
  mos = Math.round(mos * 100) / 100;
  const verdict = mos >= 4.2 ? "excellent" : mos >= 3.8 ? "good" : mos >= 3.3 ? "fair" : "poor";
  return { mos, r: Math.round(r * 10) / 10, verdict };
}

interface Sample {
  t: number;
  txBytes: number;
  rxBytes: number;
}

const previous = new Map<string, Sample>();

export function forgetStats(callId: string): void {
  previous.delete(callId);
}

export interface CallStats extends Json {
  codec?: string;
  secure: boolean;
  tx_packets: number;
  rx_packets: number;
  tx_kbps: number;
  rx_kbps: number;
  rx_lost: number;
  rx_jitter_ms: number;
  tx_lost: number;
  rtt_ms: number;
  loss_pct: number;
  mos: number;
}

export async function callStats(callId: string, pc: RTCPeerConnection): Promise<CallStats> {
  const report = await pc.getStats();
  return statsFromReport(callId, report, performance.now());
}

/* Pure part, unit-tested with recorded reports. */
export function statsFromReport(
  callId: string,
  report: Iterable<[string, any]> | { forEach(cb: (v: any) => void): void },
  now: number,
): CallStats {
  // RTCStatsReport (a Map-like: forEach gives values) or an entries iterator
  const all: any[] = [];
  const add = (v: any) => all.push(Array.isArray(v) && v.length === 2 ? v[1] : v);
  if (report instanceof Map || (typeof RTCStatsReport !== "undefined" && report instanceof RTCStatsReport)) {
    (report as Map<string, any>).forEach((v) => all.push(v));
  } else if (typeof (report as any)[Symbol.iterator] === "function") {
    for (const v of report as Iterable<any>) add(v);
  } else {
    (report as any).forEach(add);
  }

  // A re-INVITE (hold/resume) can bring a new SSRC: the browser keeps the
  // old stream's entry and adds one. Counters are summed over all of them;
  // jitter and codec come from the most recently active one.
  const isAudio = (s: any) => (s.kind ?? s.mediaType) === "audio";
  const inbounds = all.filter((s) => s.type === "inbound-rtp" && isAudio(s));
  const outbounds = all.filter((s) => s.type === "outbound-rtp" && isAudio(s));
  const latest = (xs: any[], key: string) =>
    xs.reduce((a, b) => ((b[key] ?? 0) >= (a?.[key] ?? -1) ? b : a), undefined as any);
  const inbound = latest(inbounds, "lastPacketReceivedTimestamp") ?? inbounds[0];
  const outbound = outbounds[0];
  const sum = (xs: any[], key: string) => xs.reduce((n, x) => n + Math.max(0, x[key] ?? 0), 0);
  const remoteIn = all.find((s) => s.type === "remote-inbound-rtp" && (s.kind ?? s.mediaType) === "audio");
  const pair = all.find((s) => s.type === "candidate-pair" && (s.nominated || s.selected) && s.state === "succeeded");
  const codecStat = inbound?.codecId ? all.find((s) => s.id === inbound.codecId) : undefined;
  const transport = all.find((s) => s.type === "transport");

  const rxPackets = sum(inbounds, "packetsReceived");
  const rxLost = sum(inbounds, "packetsLost");
  const txPackets = sum(outbounds, "packetsSent");
  // RTCP round trip first; the ICE pair's value is seconds in Chrome (spec)
  // but milliseconds in Firefox: a value >= 1 is taken as ms (a real
  // round trip of a second or more is rare; RTCP replaces it within seconds)
  let pairRtt = pair?.currentRoundTripTime ?? 0;
  if (pairRtt >= 1) pairRtt /= 1000;
  const rttS = remoteIn?.roundTripTime ?? pairRtt;
  const jitterS = inbound?.jitter ?? 0;
  const txBytes = sum(outbounds, "bytesSent");
  const rxBytes = sum(inbounds, "bytesReceived");

  const prev = previous.get(callId);
  let txKbps = 0;
  let rxKbps = 0;
  if (prev && now > prev.t) {
    const dt = (now - prev.t) / 1000;
    txKbps = ((txBytes - prev.txBytes) * 8) / 1000 / dt;
    rxKbps = ((rxBytes - prev.rxBytes) * 8) / 1000 / dt;
  }
  previous.set(callId, { t: now, txBytes, rxBytes });

  const lossPct = rxPackets + rxLost > 0 ? (100 * rxLost) / (rxPackets + rxLost) : 0;
  const codec = codecStat?.mimeType
    ? `${String(codecStat.mimeType).split("/")[1]}/${codecStat.clockRate}/${codecStat.channels ?? 1}`
    : undefined;
  const mos = mosEstimate({
    rtt_ms: rttS * 1000,
    jitter_ms: jitterS * 1000,
    loss_pct: lossPct,
    codec,
  }).mos;

  return {
    ...(codec ? { codec } : {}),
    secure: transport ? transport.dtlsState === "connected" : true,
    tx_packets: txPackets,
    rx_packets: rxPackets,
    tx_kbps: round1(txKbps),
    rx_kbps: round1(rxKbps),
    rx_lost: rxLost,
    rx_jitter_ms: Math.round(jitterS * 1000),
    tx_lost: Math.max(0, remoteIn?.packetsLost ?? 0),
    rtt_ms: Math.round(rttS * 1000),
    loss_pct: round1(lossPct),
    mos,
  };
}

function round1(x: number): number {
  return Math.round(x * 10) / 10;
}
